/**
 * Biometric credential storage for Capacitor native apps.
 *
 * Composes two @aparajita Capacitor plugins:
 *   - @aparajita/capacitor-biometric-auth: Face ID / Touch ID prompt + availability
 *   - @aparajita/capacitor-secure-storage: Keychain (iOS) / Keystore (Android)
 *
 * Credentials are stored as a JSON blob keyed by server URL. Preferences
 * flags (saved, username) are also keyed by server URL so switching servers
 * doesn't show a stale biometric prompt.
 */

import { isIOS, isNativePlatform } from "./platform";

interface StoredCredentials {
  username: string;
  password: string;
}

function savedKey(server: string): string {
  return `biometric_saved:${server}`;
}

function usernameKey(server: string): string {
  return `biometric_username:${server}`;
}

function credentialsKey(server: string): string {
  return `biometric_creds:${server}`;
}

// On iOS, default to whenUnlockedThisDeviceOnly so saved credentials don't
// follow the user via iCloud Keychain migration to a second device.
let _accessConfigured = false;
async function configureSecureStorageOnce(): Promise<void> {
  if (_accessConfigured) return;
  _accessConfigured = true;
  if (!isIOS()) return;
  try {
    const { SecureStorage, KeychainAccess } = await import("@aparajita/capacitor-secure-storage");
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
  } catch {
    // Best-effort; fall back to plugin defaults.
  }
}

export async function isBiometricAvailable(): Promise<boolean> {
  if (!isNativePlatform()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const result = await BiometricAuth.checkBiometry();
    return result.isAvailable;
  } catch {
    return false;
  }
}

export async function saveCredentials(server: string, username: string, password: string): Promise<void> {
  if (!isNativePlatform()) return;
  await configureSecureStorageOnce();
  const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
  const blob: Record<string, unknown> = { username, password };
  await SecureStorage.set(credentialsKey(server), blob);
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: savedKey(server), value: "true" });
    await Preferences.set({ key: usernameKey(server), value: username });
  } catch {
    // Rollback: remove the credential if metadata storage fails.
    try {
      await SecureStorage.remove(credentialsKey(server));
    } catch {
      // Best-effort rollback.
    }
    throw new Error("Failed to save credential metadata");
  }
}

export async function getCredentials(server: string): Promise<StoredCredentials | null> {
  if (!isNativePlatform()) return null;
  try {
    await configureSecureStorageOnce();
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    // authenticate() throws BiometryError on cancel / failure / lockout.
    await BiometricAuth.authenticate({ reason: "Sign in to Knocknoc" });
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    const value = await SecureStorage.get(credentialsKey(server));
    if (value === null || typeof value !== "object") {
      // Stale Preferences flag from a prior install (e.g. legacy plugin) where
      // the saved-credentials marker exists but the actual blob does not.
      // Clear the marker so the login screen stops offering biometric until
      // the user re-saves.
      await deleteCredentials(server);
      return null;
    }
    const blob = value as Record<string, unknown>;
    if (typeof blob.username !== "string" || typeof blob.password !== "string") {
      await deleteCredentials(server);
      return null;
    }
    return { username: blob.username, password: blob.password };
  } catch {
    return null;
  }
}

export async function deleteCredentials(server: string): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    await SecureStorage.remove(credentialsKey(server));
  } catch {
    // Credentials may not exist.
  }
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.remove({ key: savedKey(server) });
  await Preferences.remove({ key: usernameKey(server) });
}

export async function hasStoredCredentials(server: string): Promise<boolean> {
  if (!isNativePlatform()) return false;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: savedKey(server) });
    return value === "true";
  } catch {
    return false;
  }
}

export async function getStoredUsername(server: string): Promise<string | null> {
  if (!isNativePlatform()) return null;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: usernameKey(server) });
    return value;
  } catch {
    return null;
  }
}
